import { useRef, useState } from "react";
import { GripVertical, Loader2, Pencil, RotateCcw, Save, Trash2, Upload, X } from "lucide-react";
import { api, apiError, fileUrl, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const GST_SLABS = [0, 5, 12, 18, 28];

/** Full in-place product editor with drag-and-drop photo replacement. */
export function ProductEditDialog({ product, categories, subcategories, onClose, onSaved }) {
  const [form, setForm] = useState(product || {});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const replacePhotos = async (files, mode = "replace") => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        const res = await uploadFile(f, "product");
        urls.push(res.url);
      }
      set({ photos: mode === "replace" ? urls : [...(form.photos || []), ...urls] });
      toast.success(mode === "replace" ? "Photo replaced" : `${urls.length} photo(s) added`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      setDragging(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!String(form.name || "").trim()) return toast.error("Product name is required");
    setSaving(true);
    try {
      const numeric = ["manufacturing_cost", "standard_price", "minimum_price", "gst_rate",
        "installation_charge", "transportation_charge", "display_order"];
      const payload = { ...form };
      delete payload.id;
      numeric.forEach((k) => (payload[k] = Number(payload[k] || 0)));
      await api.put(`/products/${product.id}`, payload);
      toast.success("Product updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const subs = subcategories?.[form.category] || [];

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl"
        data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit product — {product?.name}</DialogTitle>
          <DialogDescription>
            Change anything here. Old quotations keep the price and photo they were made with.
          </DialogDescription>
        </DialogHeader>

        <div
          className={`rounded border-2 border-dashed p-3 transition-colors ${
            dragging ? "border-primary bg-primary/5" : "border-border"}`}
          data-testid="product-photo-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); replacePhotos(e.dataTransfer.files, "replace"); }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <div className="product-image-container h-[110px] w-[150px] shrink-0 rounded">
              {form.photos?.[0]
                ? <img src={fileUrl(form.photos[0])} alt={form.name} data-testid="product-photo-preview" />
                : <span className="text-xs text-muted-foreground">No photograph</span>}
            </div>
            <div className="space-y-1.5">
              <input ref={fileRef} type="file" accept="image/*" multiple hidden
                data-testid="product-photo-input"
                onChange={(e) => replacePhotos(e.target.files, "replace")} />
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" disabled={uploading}
                  data-testid="product-replace-photo-button"
                  onClick={() => fileRef.current?.click()}>
                  {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    : <Upload className="mr-1 h-3.5 w-3.5" />}
                  Replace photo
                </Button>
                {form.photos?.length > 0 && (
                  <Button size="sm" variant="ghost" data-testid="product-clear-photo-button"
                    onClick={() => set({ photos: [] })}>
                    Remove photo
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Drag a photo here, or click Replace. The full picture is always shown, never cropped.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {[["name", "Product title"], ["sku", "SKU / Model"], ["hsn_sac", "HSN / SAC code"],
            ["standard_price", "Base price ₹ (excl. tax)"], ["minimum_price", "Minimum price ₹"],
            ["manufacturing_cost", "Manufacturing cost ₹"], ["unit", "Unit"],
            ["dimensions", "Dimensions"], ["material", "Material"], ["colour", "Colour"],
            ["warranty", "Warranty"], ["display_order", "Position # (lower shows first)"],
          ].map(([key, label]) => (
            <div key={key}>
              <div className="overline">{label}</div>
              <Input data-testid={`edit-${key}-input`} value={form[key] ?? ""}
                onChange={(e) => set({ [key]: e.target.value })} />
            </div>
          ))}
          <div>
            <div className="overline">Main category</div>
            <Select value={form.category}
              onValueChange={(v) => set({ category: v, subcategory: "" })}>
              <SelectTrigger data-testid="edit-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="overline">Sub-category</div>
            {subs.length > 0 ? (
              <Select value={form.subcategory || "none"}
                onValueChange={(v) => set({ subcategory: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="edit-subcategory-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">Not set</SelectItem>
                  {subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid="edit-subcategory-input" value={form.subcategory || ""}
                placeholder="Type a sub-category"
                onChange={(e) => set({ subcategory: e.target.value })} />
            )}
          </div>
          <div>
            <div className="overline">GST slab</div>
            <Select value={String(form.gst_rate ?? 18)}
              onValueChange={(v) => set({ gst_rate: Number(v) })}>
              <SelectTrigger data-testid="edit-gst-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GST_SLABS.map((g) => (
                  <SelectItem key={g} value={String(g)}>
                    {g === 0 ? "0% (nil rated / exempt)" : `${g}%`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <div className="overline">Description</div>
            <Textarea data-testid="edit-description-input" rows={3}
              value={form.description || ""}
              onChange={(e) => set({ description: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <div className="overline">Specifications</div>
            <div className="space-y-1.5">
              {(form.specifications || []).map((s, i) => (
                <div key={i} className="flex gap-1.5">
                  <Input placeholder="Label" value={s.label || ""}
                    data-testid={`edit-spec-label-${i}`}
                    onChange={(e) => {
                      const next = [...form.specifications];
                      next[i] = { ...next[i], label: e.target.value };
                      set({ specifications: next });
                    }} />
                  <Input placeholder="Value" value={s.value || ""}
                    data-testid={`edit-spec-value-${i}`}
                    onChange={(e) => {
                      const next = [...form.specifications];
                      next[i] = { ...next[i], value: e.target.value };
                      set({ specifications: next });
                    }} />
                  <Button variant="ghost" size="icon" data-testid={`edit-spec-remove-${i}`}
                    onClick={() => set({
                      specifications: form.specifications.filter((_, x) => x !== i) })}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="edit-add-spec-button"
                onClick={() => set({
                  specifications: [...(form.specifications || []), { label: "", value: "" }] })}>
                Add specification
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="product-edit-save-button">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Reason-first soft delete, matching the record-admin proof convention. */
export function ProductDeleteDialog({ product, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const presets = ["Wrong or duplicate entry", "No longer manufactured",
                   "Added by mistake", "Replaced by a newer model"];

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/products/${product.id}/soft-delete?reason=${encodeURIComponent(reason)}`);
      toast.success(`${data.name} moved to trash`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-delete-dialog">
        <DialogHeader>
          <DialogTitle>Move "{product?.name}" to trash?</DialogTitle>
          <DialogDescription>
            It disappears from the catalogue and from quotation item pickers. Past quotations and
            invoices stay exactly as they are, and you can restore it any time.
          </DialogDescription>
        </DialogHeader>
        <Input placeholder="Why are you deleting this? (kept as proof)"
          data-testid="product-delete-reason-input" value={reason}
          onChange={(e) => setReason(e.target.value)} />
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button key={p} type="button" data-testid={`delete-reason-${p.split(" ")[0].toLowerCase()}`}
              className="rounded-full border px-2.5 py-1 text-[11px] hover:bg-accent"
              onClick={() => setReason(p)}>{p}</button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Keep it</Button>
          <Button variant="destructive" onClick={remove} disabled={busy}
            data-testid="product-delete-confirm">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Move to trash
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Trash view: restore, or purge when nothing references the product. */
export function ProductTrash({ rows, canPurge, onChanged }) {
  const [busy, setBusy] = useState("");

  const restore = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/products/${p.id}/restore`, { reason: "" });
      toast.success(`${p.name} restored to the catalogue`);
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const purge = async (p) => {
    const reason = window.prompt(
      `Delete "${p.name}" forever? Type the reason (this cannot be undone):`, "");
    if (!reason) return;
    setBusy(p.id);
    try {
      const { data } = await api.delete(
        `/products/${p.id}/purge?reason=${encodeURIComponent(reason)}`);
      toast.success(`${data.name} permanently deleted`);
      onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!rows.length)
    return (
      <p className="py-16 text-center text-sm text-muted-foreground" data-testid="trash-empty">
        The trash is empty — no deleted products.
      </p>
    );

  return (
    <div className="stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      data-testid="trash-grid">
      {rows.map((p) => (
        <div key={p.id} className="surface overflow-hidden" data-testid={`trash-card-${p.id}`}>
          <div className="product-image-container h-40 opacity-70">
            {p.photos?.[0] ? <img src={fileUrl(p.photos[0])} alt={p.name} />
              : <span className="text-xs text-muted-foreground">No photograph</span>}
            <Badge variant="destructive" className="absolute left-2 top-2 text-[9px]">
              IN TRASH
            </Badge>
          </div>
          <div className="p-4">
            <div className="overline">{p.category}{p.subcategory ? ` • ${p.subcategory}` : ""}</div>
            <div className="mt-1 line-clamp-2 text-sm font-semibold">{p.name}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {p.delete_reason || "no reason recorded"}
              {p.deleted_by_name ? ` • by ${p.deleted_by_name}` : ""}
            </div>
            {!p.can_purge && (
              <Badge variant="outline" className="mt-2 text-[10px]"
                data-testid={`trash-locked-${p.id}`}>
                {p.purge_block_reason}
              </Badge>
            )}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                disabled={busy === p.id} data-testid={`trash-restore-${p.id}`}
                onClick={() => restore(p)}>
                <RotateCcw className="h-3 w-3" /> Restore
              </Button>
              {canPurge && (
                <Button size="sm" variant="ghost" disabled={busy === p.id}
                  className="h-7 gap-1 px-2 text-[11px] text-destructive"
                  data-testid={`trash-purge-${p.id}`} onClick={() => purge(p)}>
                  <Trash2 className="h-3 w-3" /> Delete forever
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Drag-and-drop + serial number reordering for categories. */
export function CategoryReorder({ open, onOpenChange, categories, onSaved }) {
  const [order, setOrder] = useState(categories.map((c) => c.name));
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setOrder(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/product-categories/order", { order });
      toast.success("Category order saved for everyone");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="category-reorder-dialog">
        <DialogHeader>
          <DialogTitle>Arrange categories</DialogTitle>
          <DialogDescription>
            Drag to move, or type the position number. The most used ones should sit on top.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          {order.map((name, i) => (
            <div key={name} draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
              data-testid={`category-order-row-${i}`}
              className="flex items-center gap-2 rounded border border-border px-2 py-1.5">
              <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />
              <Input className="h-7 w-14 text-center text-xs" value={i + 1}
                data-testid={`category-position-${i}`}
                onChange={(e) => move(i, Number(e.target.value) - 1)} />
              <span className="text-sm">{name}</span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="category-order-save">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              : <Save className="mr-1 h-4 w-4" />} Save order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ProductCardActions = ({ product, onEdit, onDelete, canEdit, canDelete }) => (
  <div className="absolute right-2 top-2 flex gap-1">
    {canEdit && (
      <Button size="icon" variant="secondary" className="h-7 w-7"
        data-testid={`product-edit-${product.id}`} onClick={() => onEdit(product)}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    )}
    {canDelete && (
      <Button size="icon" variant="secondary" className="h-7 w-7 text-destructive"
        data-testid={`product-delete-${product.id}`} onClick={() => onDelete(product)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    )}
  </div>
);
